/**
 * Workflow Rule Storage Router
 *
 * When USE_GOBODO_API is true, delegates to Gobodo. When false, uses
 * workflowRulesStorage (localStorage). All methods are async so callers
 * work with both backends the same way.
 */

import type { WorkflowRule } from '../types/workflowRules';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { workflowRulesStorage } from './workflowRulesStorage';
import * as gobodoWorkflowRuleService from './gobodo/gobodoWorkflowRuleService';

export async function getAllRulesAsync(): Promise<WorkflowRule[]> {
  if (USE_GOBODO_API) {
    return gobodoWorkflowRuleService.getWorkflowRules();
  }
  return Promise.resolve(workflowRulesStorage.getAllRules());
}

export async function getRuleAsync(id: string): Promise<WorkflowRule | null> {
  if (USE_GOBODO_API) {
    return gobodoWorkflowRuleService.getWorkflowRuleById(id);
  }
  return Promise.resolve(workflowRulesStorage.getRule(id));
}

export async function getActiveRulesByObjectAsync(objectApiName: string): Promise<WorkflowRule[]> {
  if (USE_GOBODO_API) {
    const rules = await gobodoWorkflowRuleService.getWorkflowRules();
    return rules.filter(r => r.objectApiName === objectApiName && r.isActive);
  }
  return Promise.resolve(workflowRulesStorage.getActiveRulesByObject(objectApiName));
}

export async function createRuleAsync(
  rule: Omit<WorkflowRule, 'id' | 'createdDate' | 'lastModified'>
): Promise<WorkflowRule> {
  if (USE_GOBODO_API) {
    return gobodoWorkflowRuleService.createWorkflowRule(rule);
  }
  return Promise.resolve(workflowRulesStorage.createRule(rule));
}

export async function updateRuleAsync(
  id: string,
  updates: Partial<WorkflowRule>
): Promise<WorkflowRule | null> {
  if (USE_GOBODO_API) {
    return gobodoWorkflowRuleService.updateWorkflowRule(id, updates);
  }
  return Promise.resolve(workflowRulesStorage.updateRule(id, updates));
}

export async function deleteRuleAsync(id: string): Promise<boolean> {
  if (USE_GOBODO_API) {
    return gobodoWorkflowRuleService.deleteWorkflowRule(id);
  }
  return Promise.resolve(workflowRulesStorage.deleteRule(id));
}
